import React from 'react';
import get from 'lodash/get';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracking-provider/index.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types/src/application.tsx';
import { getCurrentEstimationStatistic } from '@atlassian/jira-software-burn-reports-common/src/common/utils/filters/index.tsx';
import ViewEvent from '@atlassian/jira-software-burn-reports-common/src/ui/view-event/index.tsx';
import { useExperienceId } from '@atlassian/jira-software-report-charting-common/src/common/utils/experience-tracking/index.tsx';
import useTimeTrackingOptionsQuery from '@atlassian/jira-software-report-charting-common/src/services/time-tracking/index.tsx';
import useQueryParameter from '@atlassian/jira-software-report-charting-common/src/ui/use-query-parameter/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { defaultTimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/constants.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import type { BurnupChartData, SprintReportsFilters } from '../common/types';
import { useBurnupFilters } from '../controllers';
import { isPollinatorTenant } from '../feature-flags';
import { useBurnupDataQuery } from '../services/burnup-data-provider/burnup-data-query/index.tsx';
import { useBurnupFiltersQuery } from '../services/burnup-data-provider/burnup-filters-query/index.tsx';
import BurnupReportView from './main';

type Props = {
	boardId: number;
	now: Date | null;
};

export const BurnupReport = ({ boardId, now }: Props) => {
	const [state] = useBurnupFilters();
	const source = useQueryParameter('source');

	const tenantContext = useTenantContext();

	const {
		data: filtersData,
		loading: filtersLoading,
		refetch: refetchFilters,
		error: filtersError,
	} = useBurnupFiltersQuery({
		boardId,
	});

	const {
		data: chartData,
		loading: chartLoading,
		error: chartError,
		refetch: refetchChart,
	} = useBurnupDataQuery({
		boardId,
		...state.dataVariables,
	});

	const filters: SprintReportsFilters | null = get(
		filtersData,
		['boardScope', 'reports', 'burnupChart', 'filters'],
		null,
	);
	const customFieldId = get(
		filtersData,
		['boardScope', 'estimation', 'current', 'customFieldId'],
		null,
	);
	const currentEstimationType = getCurrentEstimationStatistic(customFieldId);

	const experienceId = useExperienceId(chartLoading || filtersLoading);

	const chart: BurnupChartData | null = get(
		chartData,
		['boardScope', 'reports', 'burnupChart', 'chart'],
		null,
	);

	const { data: timeTrackingOptionsData } = useTimeTrackingOptionsQuery();

	const timeTrackingOptions: TimeTrackingOptions = get(
		timeTrackingOptionsData,
		['jira', 'timeTrackingOptions'],
		defaultTimeTrackingOptions,
	);

	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="burnupChart"
			attributes={{
				referrer: source || 'direct-link',
			}}
		>
			<ViewExperienceTrackingProvider
				analyticsSource="burnup"
				application={JIRA_SOFTWARE}
				edition={tenantContext && tenantContext.appEditions.software}
				experience="viewBurnup"
				experienceId={experienceId}
				additionalAttributes={{
					synthetic: isPollinatorTenant(),
				}}
			>
				<BurnupReportView
					now={now}
					filters={filters}
					// @ts-expect-error - TS2322 - Type 'ApolloError | undefined' is not assignable to type 'ApolloError | null'.
					filtersError={filtersError}
					filtersLoading={filtersLoading}
					estimationType={currentEstimationType}
					chart={chart}
					// @ts-expect-error - TS2322 - Type 'ApolloError | undefined' is not assignable to type 'ApolloError | null'.
					chartError={chartError}
					chartLoading={chartLoading}
					onRefetchFilters={() => {
						refetchFilters();
					}}
					onRefetchChart={() => {
						refetchChart();
					}}
					timeTrackingOptions={timeTrackingOptions}
					boardId={boardId}
					slaTrackingEnabled
				/>
			</ViewExperienceTrackingProvider>
			<ViewEvent boardId={boardId} />
		</ContextualAnalyticsData>
	);
};

export default BurnupReport;
